import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Spinner, Modal, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEdit, faTrash, faSyncAlt, faBriefcase, faCheckCircle,
  faExclamationTriangle, faTimes, faClock, faUser, faGraduationCap,
  faDollarSign, faFileContract, faUsers, faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';

const API_OFFRES_URL = `${API_CONFIG2.apiUrl}offres/api_offres_entreprise.php`;

const OffresEntrepriseSingle = ({ entrepriseId }) => {
  const [offres, setOffres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    description: '',
    sexe: '',
    moyen_age: '',
    competences: '',
    niveau_etude: '',
    experience_professionnelle: '',
    type_contrat: '',
    salaire: '',
    statut: '',
    nombre: 1
  });
  const [formErrors, setFormErrors] = useState({});

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);

  // ============================================================
  // Fonctions d'API
  // ============================================================
  const fetchOffres = useCallback(async (forceRefresh = false) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (forceRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const url = `${API_OFFRES_URL}?action=list&id_entreprise=${entrepriseId}&unique=${new Date().getTime()}`;
      const res = await fetch(url, { signal: controller.signal });
      const data = await res.json();
      if (!res.ok || data.status === 'error') {
        throw new Error(data.message || 'Erreur');
      }
      setOffres(data.data);
    } catch (err) {
      if (err.name === 'AbortError') return;
      setToast({ show: true, message: err.message, variant: 'danger' });
    } finally {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [entrepriseId]);

  useEffect(() => {
    isMounted.current = true;
    if (entrepriseId) fetchOffres();
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [entrepriseId, fetchOffres]);

  const showToast = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
  };

  // ============================================================
  // Gestion du formulaire
  // ============================================================
  const resetForm = () => {
    setForm({
      description: '',
      sexe: '',
      moyen_age: '',
      competences: '',
      niveau_etude: '',
      experience_professionnelle: '',
      type_contrat: '',
      salaire: '',
      statut: '',
      nombre: 1
    });
    setFormErrors({});
    setEditingId(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`🔹 handleInputChange: ${name} = "${value}"`);
    setForm(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!form.description.trim()) errors.description = 'Description requise';
    if (!form.competences.trim()) errors.competences = 'Compétences requises';
    if (!form.niveau_etude.trim()) errors.niveau_etude = 'Niveau d\'étude requis';
    if (!form.type_contrat.trim()) errors.type_contrat = 'Type de contrat requis';
    if (form.nombre < 1) errors.nombre = 'Nombre minimum 1';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (offre) => {
    setForm({
      description: offre.description || '',
      sexe: offre.sexe || '',
      moyen_age: offre.moyen_age || '',
      competences: offre.competences || '',
      niveau_etude: offre.niveau_etude || '',
      experience_professionnelle: offre.experience_professionnelle || '',
      type_contrat: offre.type_contrat || '',
      salaire: offre.salaire || '',
      statut: offre.statut || '',
      nombre: offre.nombre || 1
    });
    setEditingId(offre.id);
    setFormErrors({});
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      id_entreprise: entrepriseId,
      description: form.description.trim(),
      sexe: form.sexe || null,
      moyen_age: form.moyen_age ? parseInt(form.moyen_age) : null,
      competences: form.competences.trim(),
      niveau_etude: form.niveau_etude.trim(),
      experience_professionnelle: form.experience_professionnelle ? parseInt(form.experience_professionnelle) : null,
      type_contrat: form.type_contrat.trim(),
      salaire: form.salaire ? parseFloat(form.salaire) : null,
      statut: form.statut, // on envoie tel quel, le backend normalisera
      nombre: parseInt(form.nombre)
    };

    console.log('📦 Payload envoyé au backend :', payload);

    try {
      let res;
      if (editingId) {
        res = await fetch(`${API_OFFRES_URL}?action=update`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, id: editingId })
        });
      } else {
        res = await fetch(`${API_OFFRES_URL}?action=create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }
      const data = await res.json();
      console.log('📨 Réponse backend :', data);
      if (!res.ok || data.status === 'error') {
        throw new Error(data.message || 'Erreur inconnue');
      }
      showToast(editingId ? 'Offre mise à jour ✓' : 'Offre créée ✓', 'success');
      setShowModal(false);
      resetForm();
      fetchOffres(true);
    } catch (err) {
      console.error('❌ Erreur handleSubmit :', err);
      showToast(err.message, 'danger');
    }
  };

  // ============================================================
  // Suppression
  // ============================================================
  const confirmDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const res = await fetch(`${API_OFFRES_URL}?action=delete&id=${deleteId}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok || data.status === 'error') {
        throw new Error(data.message || 'Erreur');
      }
      showToast('Offre supprimée ✓', 'success');
      setShowDeleteModal(false);
      setDeleteId(null);
      fetchOffres(true);
    } catch (err) {
      showToast(err.message, 'danger');
    }
  };

  const handleRefresh = () => {
    fetchOffres(true);
  };

  // ============================================================
  // Rendu
  // ============================================================
  if (loading && !offres.length) {
    return (
      <div className="offres-loading">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Chargement des offres...</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* Styles (identique à votre version, je les conserve) */
        :root {
          --bg-blue-dark: #0b1428;
          --bg-glass: rgba(20, 40, 80, 0.55);
          --border-glass: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --accent-blue: #3b82f6;
          --accent-dark: #1d4ed8;
          --accent-glow: rgba(59, 130, 246, 0.3);
          --shadow-glass: 0 12px 40px rgba(0, 0, 0, 0.5);
          --radius: 24px;
          --radius-card: 16px;
        }

        .offres-wrapper {
          background: var(--bg-glass);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid var(--border-glass);
          border-radius: var(--radius);
          padding: clamp(1rem, 2vw, 2rem);
          box-shadow: var(--shadow-glass);
          color: var(--text-primary);
          margin-top: 1.5rem;
          transition: all 0.3s ease;
        }
        .offres-wrapper:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 48px rgba(59, 130, 246, 0.08);
        }

        .offres-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 1.8rem;
        }
        .offres-header h3 {
          font-weight: 700;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: clamp(1.2rem, 2vw, 1.6rem);
          color: #fff;
          letter-spacing: 0.3px;
        }
        .offres-header h3 .badge-count {
          font-size: 0.8rem;
          font-weight: 400;
          color: var(--text-secondary);
          background: rgba(255,255,255,0.05);
          padding: 2px 12px;
          border-radius: 30px;
        }
        .offres-header .actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .monetra-btn {
          padding: 8px 18px;
          border-radius: 40px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 0.9vw, 0.9rem);
          white-space: nowrap;
          min-height: 44px;
          touch-action: manipulation;
          backdrop-filter: blur(4px);
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.2);
        }
        .monetra-btn:disabled {
          opacity: 0.5;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.danger {
          background: rgba(239, 68, 68, 0.12);
          color: #f87171;
          border: 1px solid rgba(239, 68, 68, 0.2);
        }
        .monetra-btn.danger:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.25);
        }
        .monetra-btn.icon-only {
          padding: 6px 10px;
          min-width: 38px;
          justify-content: center;
        }

        .offres-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1.2rem;
          margin-top: 0.5rem;
        }

        .offre-card {
          background: rgba(255,255,255,0.03);
          backdrop-filter: blur(4px);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: var(--radius-card);
          padding: 1.2rem 1.2rem 1rem;
          transition: all 0.3s cubic-bezier(0.2, 0, 0, 1);
          display: flex;
          flex-direction: column;
          position: relative;
          overflow: hidden;
        }
        .offre-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, var(--accent-blue), transparent);
          opacity: 0;
          transition: opacity 0.3s;
        }
        .offre-card:hover {
          background: rgba(255,255,255,0.06);
          border-color: rgba(59, 130, 246, 0.2);
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3);
        }
        .offre-card:hover::before {
          opacity: 1;
        }

        .offre-card .card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 8px;
        }
        .offre-card .badge-statut {
          display: inline-block;
          padding: 3px 14px;
          border-radius: 30px;
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          background: rgba(255,255,255,0.05);
          color: var(--text-secondary);
        }
        .badge-statut.ouverte {
          background: rgba(34, 197, 94, 0.15);
          color: #22c55e;
          border: 1px solid rgba(34, 197, 94, 0.2);
        }
        .badge-statut.fermée {
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
          border: 1px solid rgba(239, 68, 68, 0.2);
        }
        .badge-statut.en-cours {
          background: rgba(245, 158, 11, 0.15);
          color: #f59e0b;
          border: 1px solid rgba(245, 158, 11, 0.2);
        }

        .offre-card .titre {
          font-size: 1rem;
          font-weight: 600;
          color: #fff;
          margin-bottom: 8px;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .offre-card .details {
          display: flex;
          flex-direction: column;
          gap: 4px;
          margin-bottom: 12px;
        }
        .offre-card .detail {
          font-size: 0.85rem;
          color: var(--text-secondary);
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .offre-card .detail .icon {
          width: 18px;
          color: var(--text-muted);
          font-size: 0.9rem;
        }

        .offre-card .card-actions {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 8px;
          padding-top: 10px;
          border-top: 1px solid rgba(255,255,255,0.04);
          flex-wrap: wrap;
        }
        .offre-card .statut-select {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          color: var(--text-primary);
          padding: 4px 12px;
          font-size: 0.75rem;
          outline: none;
          cursor: pointer;
          transition: border 0.2s;
          flex: 1;
          min-width: 80px;
        }
        .offre-card .statut-select:focus {
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 2px rgba(59,130,246,0.2);
        }
        .offre-card .statut-select option {
          background: #0b1428;
          color: var(--text-primary);
        }
        .offre-card .btn-group {
          display: flex;
          gap: 4px;
          margin-left: auto;
        }

        .empty-message {
          text-align: center;
          padding: 3rem 0;
          color: var(--text-secondary);
        }
        .empty-message .icon {
          font-size: 2.5rem;
          color: var(--text-muted);
          margin-bottom: 1rem;
        }

        .toast-container {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 10px;
          max-width: 420px;
        }
        .toast-item {
          background: rgba(10, 20, 45, 0.9);
          backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: 16px;
          padding: 14px 20px;
          color: var(--text-primary);
          display: flex;
          align-items: center;
          gap: 14px;
          box-shadow: 0 12px 40px rgba(0,0,0,0.6);
          animation: slideIn 0.3s ease;
        }
        .toast-item.success { border-left: 4px solid var(--accent-blue); }
        .toast-item.danger { border-left: 4px solid #ef4444; }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .toast-item .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          font-size: 1.2rem;
          margin-left: auto;
          padding: 0 4px;
        }
        .toast-item .btn-close-custom:hover {
          color: #fff;
        }

        .offres-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem 0;
          color: var(--text-secondary);
        }

        .modal-glass .modal-content {
          background: rgba(10, 20, 45, 0.95);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid rgba(59, 130, 246, 0.15);
          border-radius: 28px;
          color: var(--text-primary);
          box-shadow: 0 24px 60px rgba(0,0,0,0.8);
        }
        .modal-glass .modal-header {
          border-bottom: 1px solid rgba(255,255,255,0.05);
          padding: 1.2rem 1.5rem;
        }
        .modal-glass .modal-header .modal-title {
          font-weight: 700;
          font-size: 1.25rem;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .modal-glass .modal-header .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.5;
          transition: opacity 0.2s;
        }
        .modal-glass .modal-header .btn-close:hover {
          opacity: 0.9;
        }
        .modal-glass .modal-body {
          padding: 1.5rem;
        }
        .modal-glass .modal-footer {
          border-top: 1px solid rgba(255,255,255,0.05);
          padding: 1rem 1.5rem;
          gap: 10px;
        }
        .modal-glass .form-label {
          font-size: 0.85rem;
          color: var(--text-secondary);
          font-weight: 500;
          margin-bottom: 4px;
        }
        .modal-glass .form-control,
        .modal-glass .form-select {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          color: var(--text-primary);
          border-radius: 12px;
          padding: 10px 14px;
          transition: border 0.2s, box-shadow 0.2s;
        }
        .modal-glass .form-control:focus,
        .modal-glass .form-select:focus {
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
          background: rgba(255,255,255,0.06);
        }
        .modal-glass .form-control.is-invalid,
        .modal-glass .form-select.is-invalid {
          border-color: #ef4444;
          box-shadow: 0 0 0 3px rgba(239,68,68,0.1);
        }
        .modal-glass .invalid-feedback {
          color: #f87171;
          font-size: 0.8rem;
          margin-top: 4px;
        }
        .modal-glass .form-control::placeholder {
          color: var(--text-muted);
        }
        .modal-glass .form-select option {
          background: #0b1428;
          color: var(--text-primary);
        }
        .modal-glass .btn-custom {
          padding: 8px 22px;
          border-radius: 40px;
          font-weight: 500;
          border: none;
          transition: all 0.2s;
        }
        .modal-glass .btn-custom-secondary {
          background: rgba(255,255,255,0.06);
          color: var(--text-primary);
          border: 1px solid rgba(255,255,255,0.06);
        }
        .modal-glass .btn-custom-secondary:hover {
          background: rgba(255,255,255,0.12);
        }
        .modal-glass .btn-custom-primary {
          background: var(--accent-blue);
          color: #fff;
          box-shadow: 0 6px 20px var(--accent-glow);
        }
        .modal-glass .btn-custom-primary:hover {
          background: var(--accent-dark);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }
        .modal-glass .btn-custom-danger {
          background: rgba(239, 68, 68, 0.15);
          color: #f87171;
          border: 1px solid rgba(239, 68, 68, 0.2);
        }
        .modal-glass .btn-custom-danger:hover {
          background: rgba(239, 68, 68, 0.25);
        }

        @media (max-width: 768px) {
          .offres-grid { grid-template-columns: 1fr; }
          .offres-header { flex-direction: column; align-items: stretch; }
          .offres-header .actions { justify-content: stretch; }
          .offres-header .actions .monetra-btn { flex: 1; justify-content: center; }
          .offre-card .card-actions { flex-wrap: wrap; }
          .offre-card .statut-select { flex: 1 0 100%; }
          .modal-glass .modal-body { padding: 1rem; }
        }
        @media (max-width: 480px) {
          .offre-card { padding: 1rem; }
          .monetra-btn { font-size: 0.7rem; padding: 6px 12px; min-height: 36px; }
        }
      `}</style>

      <div className="offres-wrapper">
        <div className="offres-header">
          <h3>
            <FontAwesomeIcon icon={faBriefcase} className="text-primary" />
            Offres d'emploi
            <span className="badge-count">{offres.length}</span>
          </h3>
          <div className="actions">
            <button className="monetra-btn" onClick={handleRefresh} disabled={refreshing}>
              <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} /> Actualiser
            </button>
            <button className="monetra-btn primary" onClick={openAddModal}>
              <FontAwesomeIcon icon={faPlus} /> Ajouter
            </button>
          </div>
        </div>

        {offres.length === 0 ? (
          <div className="empty-message">
            <div className="icon"><FontAwesomeIcon icon={faInfoCircle} /></div>
            <p>Aucune offre pour cette entreprise.</p>
            <button className="monetra-btn primary" onClick={openAddModal} style={{ marginTop: '0.5rem' }}>
              <FontAwesomeIcon icon={faPlus} /> Créer une offre
            </button>
          </div>
        ) : (
          <div className="offres-grid">
            {offres.map(offre => (
              <div key={offre.id} className="offre-card">
                <div className="card-header">
                  <span className={`badge-statut ${offre.statut.toLowerCase().replace(' ', '-')}`}>
                    {offre.statut}
                  </span>
                </div>
                <div className="titre">{offre.description}</div>
                <div className="details">
                  <div className="detail"><span className="icon"><FontAwesomeIcon icon={faGraduationCap} /></span> {offre.niveau_etude}</div>
                  <div className="detail"><span className="icon"><FontAwesomeIcon icon={faFileContract} /></span> {offre.type_contrat}</div>
                  <div className="detail"><span className="icon"><FontAwesomeIcon icon={faUsers} /></span> {offre.nombre} poste(s)</div>
                  <div className="detail"><span className="icon"><FontAwesomeIcon icon={faDollarSign} /></span> {offre.salaire ? `${offre.salaire} USD` : 'Non spécifié'}</div>
                  <div className="detail"><span className="icon"><FontAwesomeIcon icon={faClock} /></span> Expérience: {offre.experience_professionnelle ? `${offre.experience_professionnelle} ans` : 'Non spécifiée'}</div>
                </div>
                <div className="card-actions">
                  <select
                    className="statut-select"
                    value={offre.statut}
                    onChange={async (e) => {
                      const newStatut = e.target.value;
                      try {
                        const res = await fetch(`${API_OFFRES_URL}?action=update`, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ ...offre, statut: newStatut })
                        });
                        const data = await res.json();
                        if (data.status === 'success') {
                          showToast('Statut mis à jour', 'success');
                          fetchOffres(true);
                        } else {
                          throw new Error(data.message || 'Erreur');
                        }
                      } catch (err) {
                        showToast(err.message, 'danger');
                      }
                    }}
                  >
                    <option value="Fermée">Fermée</option>
                    <option value="Ouverte">Ouverte</option>
                    <option value="En cours">En cours</option>
                  </select>
                  <div className="btn-group">
                    <button className="monetra-btn icon-only" onClick={() => openEditModal(offre)} title="Modifier">
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button className="monetra-btn icon-only danger" onClick={() => confirmDelete(offre.id)} title="Supprimer">
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MODALE AJOUT / MODIFICATION */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered className="modal-glass" size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingId ? '✏️ Modifier l\'offre' : '➕ Nouvelle offre'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Description du poste <span className="text-danger">*</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={form.description}
                onChange={handleInputChange}
                isInvalid={!!formErrors.description}
                placeholder="Décrivez le poste, les missions, le contexte..."
              />
              <Form.Control.Feedback type="invalid">{formErrors.description}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Compétences requises <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                name="competences"
                value={form.competences}
                onChange={handleInputChange}
                isInvalid={!!formErrors.competences}
                placeholder="ex: PHP, React, SQL, leadership, gestion d'équipe..."
              />
              <Form.Control.Feedback type="invalid">{formErrors.competences}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Niveau d'étude <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                name="niveau_etude"
                value={form.niveau_etude}
                onChange={handleInputChange}
                isInvalid={!!formErrors.niveau_etude}
                placeholder="ex: Bac+2, Master, Ingénieur, Doctorat..."
              />
              <Form.Control.Feedback type="invalid">{formErrors.niveau_etude}</Form.Control.Feedback>
            </Form.Group>

            {/* SÉLECTEUR TYPE CONTRAT */}
            <Form.Group className="mb-3">
              <Form.Label>Type de contrat <span className="text-danger">*</span></Form.Label>
            <Form.Select
  name="type_contrat"
  value={form.type_contrat || ''}
  onChange={(e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }}
>
  <option value="">Sélectionner</option>
  <option value="CDI">CDI</option>
  <option value="CDD">CDD</option>
  <option value="Stage">Stage</option>
  <option value="Alternance">Alternance</option>
  <option value="Freelance">Freelance</option>
</Form.Select>
              <Form.Control.Feedback type="invalid">{formErrors.type_contrat}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Nombre de postes <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="number"
                name="nombre"
                value={form.nombre}
                onChange={handleInputChange}
                isInvalid={!!formErrors.nombre}
                min="1"
              />
              <Form.Control.Feedback type="invalid">{formErrors.nombre}</Form.Control.Feedback>
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Sexe souhaité</Form.Label>
                  <Form.Select
                    name="sexe"
                    value={form.sexe || ''}
                    onChange={handleInputChange}
                    style={{ background: 'rgba(255,255,255,0.04)', color: '#e0e7ff', border: '1px solid rgba(255,255,255,0.06)' }}
                  >
                    <option value="">Non spécifié</option>
                    <option value="Homme">Homme</option>
                    <option value="Femme">Femme</option>
                    <option value="Mixte">Mixte</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Âge moyen souhaité</Form.Label>
                  <Form.Control
                    type="number"
                    name="moyen_age"
                    value={form.moyen_age}
                    onChange={handleInputChange}
                    placeholder="ex: 30"
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Expérience (années)</Form.Label>
                  <Form.Control
                    type="number"
                    name="experience_professionnelle"
                    value={form.experience_professionnelle}
                    onChange={handleInputChange}
                    placeholder="ex: 3"
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Salaire ($)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="salaire"
                    value={form.salaire}
                    onChange={handleInputChange}
                    placeholder="ex: 35000"
                  />
                </Form.Group>
              </div>
            </div>

            {/* SÉLECTEUR STATUT */}
            <Form.Group className="mb-3">
              <Form.Label>Statut</Form.Label>
           <Form.Select
  name="statut"
  value={form.statut || 'Fermée'}
  onChange={(e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }}
>
  <option value="Fermée">Fermée</option>
  <option value="Ouverte">Ouverte</option>
  <option value="En cours">En cours</option>
</Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" className="btn-custom btn-custom-secondary" onClick={() => setShowModal(false)}>
              Annuler
            </Button>
            <Button variant="primary" className="btn-custom btn-custom-primary" type="submit">
              {editingId ? 'Enregistrer' : 'Ajouter'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* MODALE SUPPRESSION */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="modal-glass">
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">Confirmer la suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">Êtes-vous sûr de vouloir supprimer cette offre d'emploi ?</p>
          <p className="text-secondary" style={{ fontSize: '0.9rem' }}>Cette action est irréversible.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" className="btn-custom btn-custom-secondary" onClick={() => setShowDeleteModal(false)}>
            Annuler
          </Button>
          <Button variant="danger" className="btn-custom btn-custom-danger" onClick={handleDelete}>
            <FontAwesomeIcon icon={faTrash} className="me-2" /> Supprimer
          </Button>
        </Modal.Footer>
      </Modal>

      {/* TOAST */}
      {toast.show && (
        <div className="toast-container">
          <div className={`toast-item ${toast.variant}`}>
            <FontAwesomeIcon icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle} />
            <span>{toast.message}</span>
            <button className="btn-close-custom" onClick={() => setToast({ ...toast, show: false })}>✕</button>
          </div>
        </div>
      )}
    </>
  );
};

export default OffresEntrepriseSingle;